import React, { useCallback, useEffect, useRef } from 'react'
import { useLatest } from 'react-use'
import { useStore } from 'react-redux'
import {
  bumpCrossClientResultsRevision,
  selectCrossClientResultsPollingEnabled,
  selectCrossClientCountsUnavailableFor,
  giveUpCrossClientCounts,
  isTerminalEvaluationStatus,
  selectCrossClientActiveEvaluations,
  selectCrossClientResultBucketsFor,
  selectCrossClientResultBucketsRevision,
  selectCrossClientResultsExecution,
  selectShouldPollCrossClientExecutionResults,
  setCrossClientResultBuckets,
  setCrossClientResultsExecution,
  mergeCrossClientActiveEvaluations,
  startCrossClientExecutionPolling,
  stopCrossClientExecutionPolling,
} from '../../../store/crossClient/crossClientResults'
import { usePollingEffect } from '../../../apps/professional-app/fa/common/hooks/usePollingEffect'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { type RootState } from '../../../store/store'
import { selectAccountId } from '../../../store/auth/authSlice'
import { executionIsActive, executionShowsResults } from '../../../store/crossClient/utils'
import {
  useLazyGetCrossClientEvaluationsQuery,
  useLazyGetCrossClientQueryExecutionQuery,
  useLazyGetCrossClientResultBucketsQuery,
} from '../../../store/crossClient/crossClient'
import {
  CrossClientQueryEvaluationStatuses,
  type CrossClientQueryEvaluation,
  type CrossClientQueryExecution,
  type CrossClientQueryResultBucketCount,
} from '../../../models/CrossClient'
import { CCQ_READ_TRIES } from './Run/useRetryLater'

const RESULTS_POLL_INTERVAL_MS = 4000

interface CrossClientAuxiliarySnapshot {
  execution: CrossClientQueryExecution
  buckets: CrossClientQueryResultBucketCount[]
  // The counts' revision when the read started.
  bucketsRevision: number
  active: CrossClientQueryEvaluation[]
}

export interface CrossClientResultsPollerProps {
  executionId: string
}

export const CrossClientResultsPoller: React.FC<CrossClientResultsPollerProps> = ({ executionId }) => {
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()

  const accountId = useAppSelector(selectAccountId)
  const shouldPoll = useAppSelector(selectShouldPollCrossClientExecutionResults)
  const pollingEnabled = useAppSelector(selectCrossClientResultsPollingEnabled)
  const activeEvaluations = useAppSelector(selectCrossClientActiveEvaluations)
  const bucketsFor = useAppSelector(selectCrossClientResultBucketsFor)
  const storedExecution = useAppSelector(selectCrossClientResultsExecution)
  const execution = storedExecution?.id === executionId ? storedExecution : null
  const waitsForCounts = execution !== null && (executionShowsResults(execution) || executionIsActive(execution))
  // A ref, not state: the stop it goes with re-renders through the store first, and the restart must already see it.
  // Never cleared: an execution that came back missing doesn't turn up later under the same id.
  const goneExecutionIdRef = useRef<string | null>(null)
  const countsUnavailable = useAppSelector(selectCrossClientCountsUnavailableFor) === executionId
  // Rounds of polling that ended without the first counts, counted afresh for each execution, once they arrive, or once the
  // advisor tries again.
  const failedRoundsRef = useRef(0)
  useEffect(() => {
    failedRoundsRef.current = 0
  }, [executionId])

  const [triggerGetCrossClientQueryExecution] = useLazyGetCrossClientQueryExecutionQuery()
  const [triggerGetResultBuckets] = useLazyGetCrossClientResultBucketsQuery()
  const [triggerGetEvaluations] = useLazyGetCrossClientEvaluationsQuery()

  const lastUpdatedAtRef = useRef<string | null>(null)
  const activeCountRef = useRef<number | null>(null)

  const hasActive = activeEvaluations.length > 0
  // A finished search doesn't poll, so a failed first read would otherwise leave the page waiting for counts.
  const awaitsFirstCounts = bucketsFor !== executionId

  const knownActiveRef = useLatest(activeEvaluations)
  const awaitsFirstCountsRef = useLatest(awaitsFirstCounts)
  const routeExecutionIdRef = useLatest(executionId)
  // The route reuses this poller for the next execution; an answer about the previous one mustn't touch it.
  const isStale = useCallback(() => routeExecutionIdRef.current !== executionId, [routeExecutionIdRef, executionId])

  // Polling stops for good; one that never got its first counts would leave its page waiting, so it's dropped and shows as missing.
  const markGone = useCallback(() => {
    goneExecutionIdRef.current = executionId
    dispatch(stopCrossClientExecutionPolling())
    if (awaitsFirstCountsRef.current) {
      dispatch(setCrossClientResultsExecution(null))
    }
  }, [dispatch, executionId, awaitsFirstCountsRef])

  useEffect(() => {
    if (shouldPoll || hasActive || awaitsFirstCounts) {
      dispatch(startCrossClientExecutionPolling())
    } else {
      dispatch(stopCrossClientExecutionPolling())
    }
  }, [dispatch, shouldPoll, hasActive, awaitsFirstCounts])

  // Polling gives up after repeated failures; without its first counts a search that lists results can't show, so it tries
  // again for a few rounds, then leaves it to the page's Try again. One that's missing, never read, or ended with nothing to
  // list shows without counts.
  useEffect(() => {
    if (!awaitsFirstCounts || countsUnavailable) {
      failedRoundsRef.current = 0
      return
    }
    if (!waitsForCounts || goneExecutionIdRef.current === executionId || pollingEnabled) {
      return
    }
    failedRoundsRef.current += 1
    dispatch(failedRoundsRef.current >= CCQ_READ_TRIES ? giveUpCrossClientCounts(executionId) : startCrossClientExecutionPolling())
  }, [dispatch, awaitsFirstCounts, waitsForCounts, executionId, pollingEnabled, countsUnavailable])

  const fetchActiveEvaluations = useCallback(async (): Promise<CrossClientQueryEvaluation[]> => {
    const evaluations = await triggerGetEvaluations({
      accountId,
      executionId,
      statuses: [
        CrossClientQueryEvaluationStatuses.PENDING,
        CrossClientQueryEvaluationStatuses.QUEUED,
        CrossClientQueryEvaluationStatuses.RUNNING,
        CrossClientQueryEvaluationStatuses.CANCELING,
      ],
      expand: ['progress'],
    }).unwrap()
    return evaluations?.data ?? []
  }, [accountId, executionId, triggerGetEvaluations])

  const fetchAuxiliary = useCallback(async (): Promise<CrossClientAuxiliarySnapshot | null> => {
    const bucketsRevision = selectCrossClientResultBucketsRevision(store.getState())
    const [execution, buckets, active] = await Promise.all([
      triggerGetCrossClientQueryExecution({ accountId, executionId, expand: ['matches', 'progress', 'evaluationCounts'] }).unwrap(),
      triggerGetResultBuckets({ accountId, executionId }).unwrap(),
      fetchActiveEvaluations(),
    ])
    if (!execution) {
      return null
    }
    return { execution, buckets: buckets ?? [], bucketsRevision, active }
  }, [store, accountId, executionId, triggerGetCrossClientQueryExecution, triggerGetResultBuckets, fetchActiveEvaluations])

  const publishAuxiliary = useCallback(
    ({ execution, buckets, bucketsRevision, active }: CrossClientAuxiliarySnapshot) => {
      dispatch(setCrossClientResultsExecution(execution))
      dispatch(setCrossClientResultBuckets(buckets, bucketsRevision))
      dispatch(mergeCrossClientActiveEvaluations(active))
    },
    [dispatch],
  )

  const knownActiveIds = useCallback(() => new Set(knownActiveRef.current.map((each) => each.id)), [knownActiveRef])

  // Started while a read was in flight, so the read may predate them; the next tick decides on them.
  const withStartedMeanwhile = useCallback(
    (knownAtStart: Set<string>, active: CrossClientQueryEvaluation[]) => [
      ...active,
      ...knownActiveRef.current.filter(
        (known) => !knownAtStart.has(known.id) && !isTerminalEvaluationStatus(known.status) && !active.some((each) => each.id === known.id),
      ),
    ],
    [knownActiveRef],
  )

  const refreshAuxiliary = useCallback(async (): Promise<{ activeCount: number; lastUpdatedAt: string | null } | null> => {
    const knownAtStart = knownActiveIds()
    const snapshot = await fetchAuxiliary()
    if (isStale()) {
      return null
    }
    if (!snapshot) {
      markGone()
      return null
    }
    const active = withStartedMeanwhile(knownAtStart, snapshot.active)
    publishAuxiliary({ ...snapshot, active })
    return { activeCount: active.length, lastUpdatedAt: snapshot.execution.progress?.lastUpdatedAt ?? null }
  }, [fetchAuxiliary, isStale, markGone, publishAuxiliary, knownActiveIds, withStartedMeanwhile])

  useEffect(() => {
    refreshAuxiliary()
      .then((refreshed) => {
        if (refreshed) {
          activeCountRef.current = refreshed.activeCount
          lastUpdatedAtRef.current = refreshed.lastUpdatedAt
        }
      })
      .catch(console.error)
  }, [refreshAuxiliary])

  const pollTick = useCallback(async () => {
    const knownAtStart = knownActiveIds()
    const snapshot = await fetchAuxiliary()
    if (isStale()) {
      return
    }
    if (!snapshot) {
      markGone()
      return
    }
    let active = snapshot.active
    const lastUpdatedAt = snapshot.execution.progress?.lastUpdatedAt ?? null
    const previousActive = activeCountRef.current
    const activeShrank = previousActive !== null && active.length < previousActive
    const progressChanged = lastUpdatedAt !== lastUpdatedAtRef.current
    // An evaluation started while this snapshot was in flight isn't in it yet; publishing it as is would drop it.
    const missesKnownActive = knownActiveRef.current.some(
      (known) => !isTerminalEvaluationStatus(known.status) && !snapshot.active.some((each) => each.id === known.id),
    )
    if (activeShrank || progressChanged) {
      dispatch(bumpCrossClientResultsRevision())
    }
    if (activeShrank || progressChanged || missesKnownActive) {
      // Re-read: the snapshot predates the lists' refetch and could drop a just-started evaluation.
      active = await fetchActiveEvaluations()
      if (isStale()) {
        return
      }
    }
    active = withStartedMeanwhile(knownAtStart, active)
    activeCountRef.current = active.length
    lastUpdatedAtRef.current = lastUpdatedAt
    publishAuxiliary({ ...snapshot, active })
  }, [dispatch, fetchAuxiliary, fetchActiveEvaluations, isStale, markGone, publishAuxiliary, knownActiveRef, knownActiveIds, withStartedMeanwhile])

  const handlePollingStopped = useCallback(async () => {
    dispatch(stopCrossClientExecutionPolling())
  }, [dispatch])

  const { start: startResultsPolling, stop: stopResultsPolling } = usePollingEffect(pollTick, [], {
    interval: RESULTS_POLL_INTERVAL_MS,
    leading: false,
    onCleanUp: handlePollingStopped,
  })

  useEffect(() => {
    if (pollingEnabled) {
      startResultsPolling()
    } else {
      stopResultsPolling()
    }
  }, [pollingEnabled, startResultsPolling, stopResultsPolling])

  return null
}
